import { useState, useCallback } from 'react';
import { useURLParams } from './useURLParams';

export interface PaginationConfig {
  defaultPageSize?: number;
  pageSizeOptions?: number[];
}

export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PaginationActions {
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  updatePagination: (pagination: { total: number; totalPages: number; hasNextPage: boolean; hasPrevPage: boolean }) => void;
}

export function usePagination(config: PaginationConfig = {}): [PaginationState, PaginationActions] {
  const { defaultPageSize = 50 } = config;
  
  const { params, updateParams } = useURLParams({
    initialValues: {
      page: 1,
      pageSize: defaultPageSize
    },
    paramTypes: {
      page: 'number',
      pageSize: 'number'
    }
  });

  const [paginationInfo, setPaginationInfo] = useState({
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  const setPage = useCallback((page: number) => {
    updateParams({ page });
  }, [updateParams]);

  const setPageSize = useCallback((size: number) => {
    updateParams({ 
      pageSize: size,
      page: 1 // Reset to first page when page size changes
    });
  }, [updateParams]);

  const goToFirstPage = useCallback(() => {
    setPage(1);
  }, [setPage]);

  const goToLastPage = useCallback(() => {
    setPage(paginationInfo.totalPages);
  }, [setPage, paginationInfo.totalPages]);

  const goToNextPage = useCallback(() => {
    if (paginationInfo.hasNextPage) {
      setPage(params.page + 1);
    }
  }, [setPage, params.page, paginationInfo.hasNextPage]);

  const goToPrevPage = useCallback(() => {
    if (paginationInfo.hasPrevPage) {
      setPage(params.page - 1);
    }
  }, [setPage, params.page, paginationInfo.hasPrevPage]);

  const updatePagination = useCallback((pagination: { 
    total: number; 
    totalPages: number; 
    hasNextPage: boolean; 
    hasPrevPage: boolean 
  }) => {
    setPaginationInfo({
      totalPages: pagination.totalPages,
      totalCount: pagination.total,
      hasNextPage: pagination.hasNextPage,
      hasPrevPage: pagination.hasPrevPage
    });
  }, []);

  const state: PaginationState = {
    currentPage: params.page,
    pageSize: params.pageSize,
    totalPages: paginationInfo.totalPages,
    totalCount: paginationInfo.totalCount,
    hasNextPage: paginationInfo.hasNextPage,
    hasPrevPage: paginationInfo.hasPrevPage
  };

  const actions: PaginationActions = {
    setPage,
    setPageSize,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPrevPage,
    updatePagination
  };

  return [state, actions];
}